/**
 * Parses the GA4 session id out of a `_ga_<measurementId>` cookie value.
 *
 * The cookie has the shape `GS2.1.s<sessionStartTimestamp>$o<n>$g<n>$t<n>$j<n>$l<n>$h<hash>`.
 * GA4's Measurement Protocol `sid` parameter expects that session-start
 * timestamp (the `s` segment) — it's what ties a server-fired hit to the
 * browser's already-classified session for channel attribution.
 *
 * Returns `null` when the value is missing or not in the expected format.
 *
 * Split out from `ga-client-id.ts` (which imports `next/headers`, a
 * server-only module) so it can be imported from client code too — see
 * `parse-ga-client-id.ts`.
 */
export function parseGaSessionId(
	gaSessionCookieValue: string | undefined
): string | null {
	if (!gaSessionCookieValue) {
		return null;
	}
	const match = /^GS\d+\.\d+\.s(\d+)/.exec(gaSessionCookieValue);
	return match?.[1] ?? null;
}

/**
 * GA4 names its session cookie `_ga_<measurementId>` with the `G-` prefix
 * stripped, e.g. measurement id `G-YYRM0H8SP4` -> cookie `_ga_YYRM0H8SP4`.
 */
export function gaSessionCookieName(measurementId: string): string {
	return `_ga_${measurementId.replace(/^G-/, "")}`;
}
